import { lazy, type ReactElement, Suspense } from "react";

import { AppBootScreen } from "@/components/AppBootScreen";
import ErrorBoundary from "@/ErrorBoundary";
import { useAppAuth } from "@/hooks/useAppAuth";
import { useAppPathwayBundles } from "@/hooks/useAppPathwayBundles";
import { lazyPage } from "@/routes/lazyPage";
import { AppNotFoundIfUnmatched, AppRouteMatchScope } from "@/routes/routeMatchScope";

import { useLocation } from "wouter";

import { isPublicAppPath } from "./publicAppPaths";
import PublicRoutesSwitch from "./publicRoutes";
import { RouteErrorFallback } from "./RouteErrorFallback";

const SharedProtectedRoutesSwitch = lazy(() => import("./sharedProtectedRoutes"));
const ClientPathwayRoutesSwitch = lazy(() => import("./clientPathwayRoutes"));
const SfsPathwayRoutesSwitch = lazy(() => import("./sfsPathwayRoutes"));
const ReportingRoutesSwitch = lazy(() => import("./reportingRoutes"));
const SignedInPathwayFallback = lazy(() =>
  import("./SignedInPathwayFallback").then((module) => ({
    default: module.SignedInPathwayFallback,
  })),
);

const NotFound = lazyPage(() => import("@/pages/not-found"));

export function PathwayRoutesFallback(): ReactElement | null {
  const [location] = useLocation();
  const { authenticated, clerkLoaded } = useAppAuth();

  // Sign-in and other public pages must stay visible while the signed-in shell
  // downloads. A full-screen boot fallback here covers the form until reload,
  // when that shell is already in the cache.
  if (isPublicAppPath(location)) {
    return null;
  }

  if (authenticated && clerkLoaded) {
    return (
      <Suspense fallback={<AppBootScreen message="Loading application…" />}>
        <SignedInPathwayFallback />
      </Suspense>
    );
  }

  return <AppBootScreen message="Loading application…" />;
}

export function AppRoutes(): ReactElement {
  const [location] = useLocation();
  const { loadClientPathway, loadSfsPathway, loadReportingPathway } = useAppPathwayBundles();
  const onPublicPath = isPublicAppPath(location);

  // Each scope sits inside the boundary that catches its lazy routes. A lazy
  // page sets the match flag and then suspends; React retries only that
  // boundary. Public pages stay outside the signed-in shell boundary so the
  // form stays visible while that shell downloads.
  return (
    <>
      <Suspense fallback={<AppBootScreen />}>
        <AppRouteMatchScope>
          <PublicRoutesSwitch />
          {onPublicPath ? (
            <AppNotFoundIfUnmatched>
              <NotFound />
            </AppNotFoundIfUnmatched>
          ) : null}
        </AppRouteMatchScope>
      </Suspense>
      <ErrorBoundary
        resetKeys={[location]}
        fallback={({ error }) => <RouteErrorFallback error={error} />}
      >
        <Suspense fallback={<PathwayRoutesFallback />}>
          <AppRouteMatchScope>
            <SharedProtectedRoutesSwitch />
            {loadClientPathway ? <ClientPathwayRoutesSwitch /> : null}
            {loadSfsPathway ? <SfsPathwayRoutesSwitch /> : null}
            {loadReportingPathway ? <ReportingRoutesSwitch /> : null}
            {onPublicPath ? null : (
              <AppNotFoundIfUnmatched>
                <NotFound />
              </AppNotFoundIfUnmatched>
            )}
          </AppRouteMatchScope>
        </Suspense>
      </ErrorBoundary>
    </>
  );
}
